import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadCloud, UserCircle, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user } = useAuth();
  const [signatureInfo, setSignatureInfo] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const signatureRef = useRef(null);
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const loadSignature = () => {
    api.get("/settings/signature").then((r) => setSignatureInfo(r.data));
  };

  useEffect(loadSignature, []);

  const triggerSignatureUpload = () => {
    if (signatureInfo?.configured && !window.confirm(
      "Ya tienes una firma configurada. Si subes una nueva, la anterior se perderá.\n\n¿Continuar?"
    )) return;
    signatureRef.current?.click();
  };

  const uploadSignature = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) return toast.error("Selecciona una firma PNG o JPG");
    setSignatureLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSignatureInfo({ configured: true, source: "user", ...data });
      toast.success("Firma actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir la firma");
    } finally {
      setSignatureLoading(false);
      if (signatureRef.current) signatureRef.current.value = "";
    }
  };

  const changePassword = async () => {
    if (!pwForm.old_password || !pwForm.new_password) return toast.error("Rellena todos los campos");
    if (pwForm.new_password.length < 6) return toast.error("La nueva contraseña debe tener al menos 6 caracteres");
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Las contraseñas no coinciden");
    setPwLoading(true);
    try {
      await api.put("/auth/password", { old_password: pwForm.old_password, new_password: pwForm.new_password });
      toast.success("Contraseña cambiada");
      setPwForm({ old_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div data-testid="account-page">
      <PageHeader title="Cuenta" subtitle="Preferencias personales y firma" />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="data-card p-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <UserCircle className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="data-card p-5 xl:col-span-2">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="font-display font-medium text-lg">Firma de contratos</h3>
              <p className="text-sm text-muted-foreground mt-1">Esta firma se usará solo para los PDF firmados que generes tú.</p>
              <p className="text-xs text-muted-foreground mt-2">
                Estado: {signatureInfo?.configured ? `Configurada${signatureInfo?.filename ? ` (${signatureInfo.filename})` : ""}` : "Sin configurar"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {signatureInfo?.configured && (
                <img src={`${API}/settings/signature.png?t=${signatureInfo?.updated_at || "fallback"}`} alt="Firma actual" className="h-16 max-w-56 rounded-md border border-border bg-white object-contain p-2 shadow-sm" />
              )}
              <input ref={signatureRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadSignature(e.target.files?.[0])} />
              <Button onClick={triggerSignatureUpload} disabled={signatureLoading} data-testid="account-signature-upload-button">
                <UploadCloud className="h-4 w-4 mr-1.5" /> Subir firma
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="data-card p-5 mt-5 max-w-xl">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-display font-medium text-lg">Cambiar contraseña</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Contraseña actual</Label>
            <Input type="password" value={pwForm.old_password} onChange={(e) => setPwForm((f) => ({ ...f, old_password: e.target.value }))} />
          </div>
          <div>
            <Label>Nueva contraseña</Label>
            <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))} />
          </div>
          <div>
            <Label>Confirmar nueva contraseña</Label>
            <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} />
          </div>
          <Button onClick={changePassword} disabled={pwLoading}>
            {pwLoading ? "Cambiando…" : "Cambiar contraseña"}
          </Button>
        </div>
      </div>
    </div>
  );
}
